import { motion, useAnimationControls, useReducedMotion } from "motion/react";
import { useEffect, useRef, type ReactNode } from "react";

import { cn } from "../../lib/cn";

export type BadgeStatus = "success" | "error" | "warning" | "info" | "neutral";

const soft: Record<BadgeStatus, string> = {
  success: "bg-success-surface text-success-text",
  error: "bg-error-surface text-error-text",
  warning: "bg-warning-surface text-warning-text",
  info: "bg-info-surface text-info-text",
  neutral: "bg-fill text-text-secondary",
};

const base = "inline-flex items-center rounded-full px-2.5 py-0.5 text-label";

export type BadgeProps = {
  status: BadgeStatus;
  className?: string;
  children: ReactNode;
};

export function Badge({ status, className, children }: BadgeProps) {
  const reduce = useReducedMotion();
  const controls = useAnimationControls();
  const first = useRef(true);

  useEffect(() => {
    if (reduce) return;
    if (first.current) {
      first.current = false;
      return;
    }
    // Restrained: pop only when the content changes, never on mount.
    controls.start({
      scale: [1, 1.18, 1],
      transition: { duration: 0.28, ease: "easeOut" },
    });
  }, [children, controls, reduce]);

  if (reduce) {
    return (
      <span className={cn(base, soft[status], className)}>{children}</span>
    );
  }

  return (
    <motion.span
      className={cn(base, soft[status], className)}
      animate={controls}
    >
      {children}
    </motion.span>
  );
}
